/**
 * Smooth and efficient zooming and panning (van Wijk & Nuij, 2003): the camera path between two views — a centre
 * point and a visible width each — that feels shortest. It zooms out while it travels, so that the target comes into
 * view early, and zooms in on it at the end, instead of moving the centre linearly while the width shrinks
 * exponentially (the target then appears only in the very last moment — maintainer feedback, Sprint 3). Pure.
 */

export interface ZoomPanPath {
  /** Path length in the paper's units; the parameter s runs over [0, S]. */
  readonly S: number;
  /** Distance travelled along the line from the start centre towards the end centre at s (0 … u1). */
  u(s: number): number;
  /** Visible width at s. */
  w(s: number): number;
}

/** The paper's trade-off between zooming and panning; √2 is its recommendation, smaller zooms out less. */
export const DEFAULT_RHO = 1.3;

/**
 * Path from width `w0` to width `w1` while the centre travels `u1` (same units as the widths).
 */
export function zoomPanPath(w0: number, w1: number, u1: number, rho = DEFAULT_RHO): ZoomPanPath {
  const r2 = rho * rho;
  // no travel (or travel too small to matter): a pure logarithmic zoom
  if (!(u1 > 1e-9 * Math.max(w0, w1))) {
    const k = Math.log(w1 / w0);
    const S = Math.abs(k) / rho;
    return {
      S,
      u: () => 0,
      w: (s) => (S > 0 ? w0 * Math.exp((k * s) / S) : w0),
    };
  }
  const b0 = (w1 * w1 - w0 * w0 + r2 * r2 * u1 * u1) / (2 * w0 * r2 * u1);
  const b1 = (w1 * w1 - w0 * w0 - r2 * r2 * u1 * u1) / (2 * w1 * r2 * u1);
  // r = ln(−b + √(b² + 1)) = −asinh(b), without the cancellation for large b
  const r0 = -Math.asinh(b0);
  const r1 = -Math.asinh(b1);
  const S = (r1 - r0) / rho;
  const c0 = Math.cosh(r0);
  const s0 = Math.sinh(r0);
  return {
    S,
    u: (s) => (s >= S ? u1 : (w0 / r2) * (c0 * Math.tanh(rho * s + r0) - s0)),
    w: (s) => (s >= S ? w1 : (w0 * c0) / Math.cosh(rho * s + r0)),
  };
}
